import React, { useState, useEffect } from 'react';
import { InteractivePythonBlock } from './InteractivePythonBlock';

interface CodeSnippetProps {
  src: string;  // Path relative to snippets directory
  title?: string;
  editable?: boolean;
  showLineNumbers?: boolean;
}

const CodeSnippet: React.FC<CodeSnippetProps> = ({ 
  src, 
  title,
  editable = true,
  showLineNumbers = false 
}) => {
  const [code, setCode] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSnippet = async () => {
      try {
        setLoading(true);
        setError(null);
        const snippetPath = `/snippets/${src}.py`;
        const response = await fetch(snippetPath);
        if (!response.ok) {
          throw new Error(`Failed to load snippet: ${snippetPath}`);
        }
        const codeContent = await response.text();
        setCode(codeContent);
      } catch (err) {
        console.error('Error loading snippet:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    loadSnippet();
  }, [src]);

  if (loading) {
    return (
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-8">
        <div className="flex items-center justify-center space-x-2">
          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-600 dark:text-gray-400">Loading code snippet...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-red-200 dark:border-red-800 rounded-lg p-4 bg-red-50 dark:bg-red-900/20">
        <div className="flex items-start space-x-2">
          <span className="text-red-600 dark:text-red-400">⚠️</span>
          <div>
            <p className="text-red-800 dark:text-red-200 font-medium">Failed to load code snippet</p>
            <p className="text-red-600 dark:text-red-400 text-sm mt-1">{error}</p>
            <p className="text-red-600 dark:text-red-400 text-sm">Path: {src}.py</p>
          </div>
        </div>
      </div>
    );
  }

  const filename = src.split('/').pop() + '.py';
  const displayTitle = title || filename;

  return (
    <InteractivePythonBlock 
      filename={filename}
      title={displayTitle}
      editable={editable}
    >
      {code}
    </InteractivePythonBlock>
  );
};

export { CodeSnippet };
export default CodeSnippet;